import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Router } from '@angular/router';

import { AuthService } from '../services/auth.service';
import { Observable, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthInterceptor implements HttpInterceptor {
  private readonly router = inject(Router);
  
  // Module endpoints that indicate permission/authorization errors (not authentication errors)
  private readonly moduleEndpoints = [
    '/deals',
    '/contacts',
    '/companies',
    '/leads',
    '/person',
    '/activity',
    '/messaging',
    '/tasks',
    '/calls',
    '/notes',
    '/lists',
  ];

  // Auth endpoints where 401/403 should trigger logout (authentication errors)
  private readonly authEndpoints = [
    '/auth/',
    '/admin/sign-in',
    '/profile/me',
    '/workspace-member/',
  ];

  constructor(private auth: AuthService) {}

  /**
   * Check if the error is a permission/authorization error (should NOT logout)
   * vs an authentication error (should logout)
   */
  private isPermissionError(error: HttpErrorResponse, requestUrl: string): boolean {
    // Check if error is from a specific known permission error code
    if (error.status === 403 && error.error?.err === 'authErrorUiali') {
      return true;
    }

    // Check if the request is to a module endpoint (indicating permission issue)
    const isModuleEndpoint = this.moduleEndpoints.some(endpoint => 
      requestUrl.includes(endpoint)
    );

    // If it's a 403 from a module endpoint, it's a permission error
    if (error.status === 403 && isModuleEndpoint) {
      return true;
    }

    // If it's a 401 from a module endpoint, treat as permission error (to be safe)
    // This prevents logout when user has valid token but lacks permissions
    if (error.status === 401 && isModuleEndpoint) {
      return true;
    }

    // Check error message for permission-related keywords
    const errorMessage = this.getErrorMessage(error).toLowerCase();
    const permissionKeywords = [
      'permission',
      'forbidden',
      'access denied',
      'not authorized',
      'insufficient',
      'canread',
      'canupdate',
      'read permission',
      'update permission',
      'does not have',
      'missing permission',
    ];

    const hasPermissionKeyword = permissionKeywords.some(keyword => 
      errorMessage.includes(keyword)
    );

    if (hasPermissionKeyword && (error.status === 401 || error.status === 403)) {
      return true;
    }

    return false;
  }

  /**
   * Check if the request is to an authentication endpoint
   * (where 401/403 should trigger logout)
   */
  private isAuthEndpoint(requestUrl: string): boolean {
    return this.authEndpoints.some(endpoint => requestUrl.includes(endpoint));
  }

  /**
   * Extract error message from error response
   */
  private getErrorMessage(error: HttpErrorResponse): string {
    if (error.error) {
      if (typeof error.error === 'string') {
        return error.error;
      }
      if (typeof error.error === 'object') {
        return error.error.message || error.error.error || error.error.detail || '';
      }
    }
    return error.message || '';
  }

  intercept(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    request: HttpRequest<any>,
    next: HttpHandler,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Observable<HttpEvent<any>> {
    const token = localStorage.getItem('token');
    request = request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
    return next.handle(request).pipe(
      tap(
        () => {},
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (err: any) => {
          if (err instanceof HttpErrorResponse) {
            // Handle 401 (Unauthorized) and 403 (Forbidden) errors
            if (err.status === 401 || err.status === 403) {
              // Check if this is a permission/authorization error (should NOT logout)
              if (this.isPermissionError(err, request.url)) {
                // Permission error - do NOT logout, let component handle it
                return;
              }

              // Skip logout for admin endpoints and export endpoints
              const isAdminEndpoint = request.url.includes('/admin/');
              const isAdminSignIn = request.url.includes('/admin/sign-in');
              const isExportEndpoint = request.url.includes('/export');
              const currentRoute = this.router.url;
              const isOnAdminPage = currentRoute.startsWith('/admin');
            
              // Only logout for authentication errors (not permission errors)
              // At this point, we know it's NOT a permission error, so it's an auth error
              // Logout unless it's an admin/export endpoint
              if (!isAdminEndpoint && !isAdminSignIn && !isOnAdminPage && !isExportEndpoint) {
                this.auth.logout();
              }
            }
          }
        },
      ),
    );
  }
}
